import { DOCUMENT, isPlatformBrowser } from '@angular/common';
import { Injectable, PLATFORM_ID, inject } from '@angular/core';

import { ScriptLoaderService } from '@stair-craft/common/util';

import { ITracker } from './tracker';
import { WINDOW } from './window.token';

export const YM_COUNTER_ID: number = 108667709;

const METRIKA_SCRIPT_ID: string = 'sc-yandex-metrika';
const METRIKA_SCRIPT_SRC: string = `https://mc.yandex.ru/metrika/tag.js?id=${String(YM_COUNTER_ID)}`;

export interface IMetrikaHitOptions {
    title?: string;
    referer?: string;
    params?: Record<string, unknown>;
}

export interface IMetrikaLinkOptions {
    title?: string;
}

/**
 * Yandex.Metrika facade. On browser bootstrap this service:
 *   1. installs a synchronous `window.ym` stub that queues calls into `ym.a`
 *      (so `hit`/`reachGoal` invoked before tag.js loads are not lost);
 *   2. fires `ym(id, 'init', {...})` into that queue;
 *   3. delegates actual tag.js loading to ScriptLoaderService with a
 *      triple-trigger strategy (user-interaction / rIC / timeout);
 *   4. skips everything if the visitor looks like a bot.
 *
 * SSR-safe: bootstrap short-circuits when not in browser, and every public
 * method guards on `typeof window.ym === 'function'`.
 */
@Injectable({ providedIn: 'root' })
export class MetrikaService implements ITracker {
    readonly #windowRef: Window = inject(WINDOW);
    readonly #doc: Document = inject(DOCUMENT);
    readonly #platformId: object = inject(PLATFORM_ID);
    readonly #scriptLoader: ScriptLoaderService = inject(ScriptLoaderService);

    #bootstrapped: boolean = false;

    public bootstrap(): void {
        if (this.#bootstrapped) {
            return;
        }
        if (!isPlatformBrowser(this.#platformId)) {
            return;
        }
        this.#bootstrapped = true;

        this.#installStub();
        this.#windowRef.ym?.(YM_COUNTER_ID, 'init', {
            ssr: true,
            webvisor: true,
            clickmap: true,
            ecommerce: 'dataLayer',
            accurateTrackBounce: true,
            trackLinks: true,
            defer: true,
        });

        void this.#scriptLoader.loadDeferred(
            { id: METRIKA_SCRIPT_ID, src: METRIKA_SCRIPT_SRC, async: true },
            [{ type: 'user-interaction' }, { type: 'idle', timeoutMs: 4000 }, { type: 'timeout', delayMs: 3000 }],
            true
        );
    }

    public hit(url: string, options?: IMetrikaHitOptions): void {
        if (typeof this.#windowRef.ym !== 'function') {
            return;
        }
        this.#windowRef.ym(YM_COUNTER_ID, 'hit', url, {
            title: options?.title ?? this.#doc.title,
            referer: options?.referer ?? this.#doc.referrer,
            params: options?.params,
        });
    }

    public reachGoal(target: string, params?: Record<string, unknown>): void {
        if (typeof this.#windowRef.ym !== 'function') {
            return;
        }
        this.#windowRef.ym(YM_COUNTER_ID, 'reachGoal', target, params);
    }

    public setUserID(id: string): void {
        if (typeof this.#windowRef.ym !== 'function') {
            return;
        }
        this.#windowRef.ym(YM_COUNTER_ID, 'setUserID', id);
    }

    // ── Tracker interface ────────────────────────────────────────────────
    // Vendor-neutral surface; delegates to the Metrika-specific methods
    // above. `setUser`'s `props` argument is intentionally ignored —
    // Metrika has no direct equivalent of GTM user_properties.

    public pageView(url: string, title?: string): void {
        this.hit(url, { title });
    }

    public event(name: string, params?: Record<string, unknown>): void {
        this.reachGoal(name, params);
    }

    public setUser(userId: string): void {
        this.setUserID(userId);
    }

    public userParams(params: Record<string, unknown>): void {
        if (typeof this.#windowRef.ym !== 'function') {
            return;
        }
        this.#windowRef.ym(YM_COUNTER_ID, 'userParams', params);
    }

    public params(params: Record<string, unknown>): void {
        if (typeof this.#windowRef.ym !== 'function') {
            return;
        }
        this.#windowRef.ym(YM_COUNTER_ID, 'params', params);
    }

    public extLink(url: string, options?: IMetrikaLinkOptions): void {
        if (typeof this.#windowRef.ym !== 'function') {
            return;
        }
        this.#windowRef.ym(YM_COUNTER_ID, 'extLink', url, options);
    }

    public file(url: string, options?: IMetrikaLinkOptions): void {
        if (typeof this.#windowRef.ym !== 'function') {
            return;
        }
        this.#windowRef.ym(YM_COUNTER_ID, 'file', url, options);
    }

    public notBounce(): void {
        if (typeof this.#windowRef.ym !== 'function') {
            return;
        }
        this.#windowRef.ym(YM_COUNTER_ID, 'notBounce');
    }

    #installStub(): void {
        if (typeof this.#windowRef.ym === 'function') {
            return;
        }
        interface IYmStub {
            (counterId: number, action: string, ...args: unknown[]): void;
            a?: unknown[][];
            l?: number;
        }
        const stub: IYmStub = function (...args: unknown[]): void {
            stub.a = stub.a ?? [];
            stub.a.push(args);
        };
        stub.l = Date.now();
        this.#windowRef.ym = stub;
    }
}
